/**
 * Store: Screen
 * ------------------------------------------------------------------------------
 * Global store for window/viewport based states.
 *
 * @namespace useScreenStore
 */
import { defineStore } from 'pinia'

import breakpoints from '~/helpers/breakpoints'

export const useScreenStore = defineStore({
  id: 'screen',

  state: () => {
    return {
      breakpoint: 'tiny',
      height: 0,
      mobile: (!window.matchMedia(`(min-width: ${breakpoints.large})`).matches),
      orientation: 'portrait',
      width: 0,
    }
  },

  actions: {

    /**
     * Store the updated screen information.
     * @param {Object} screen - New 'screen' information definition.
     */
    setScreen(screen) {
      this.breakpoint = screen.breakpoint
      this.height = screen.height
      this.mobile = screen.mobile
      this.orientation = screen.orientation
      this.width = screen.width
    },

    /**
     * Handle resize events for the viewport, which
     * will emit to each component.
     * @param {Had} event
     */
    handleResize() {

      /**
       * Remove 'Checkout' breakpoint as it's not relevant outside of Checkout.
       */
      const modifiedBreakpoints = breakpoints
      delete modifiedBreakpoints.checkout_large

      const screen = {
        breakpoint: 'tiny',
        height: 0,
        mobile: true,
        orientation: 'portrait',
        screen: {},
        width: 0,
      }

      /**
       * Set dimensions.
       */
      screen.height = visualViewport.height
      screen.width = visualViewport.width

      /**
       * Set current breakpoint.
       */
      Object.keys(modifiedBreakpoints).forEach((key) => {
        const matches = Boolean(
          window.matchMedia(`(min-width: ${modifiedBreakpoints[key]})`).matches,
        )

        if (matches) {
          screen.breakpoint = key
        }
      })

      /**
       * Set mobile state.
       */
      screen.mobile = Boolean(
        !window.matchMedia(`(min-width: ${modifiedBreakpoints.large})`).matches,
      )

      /**
       * Set orientation.
       */
      let orientation = 'portrait'

      if (screen.width > screen.height) {
        orientation = 'landscape'
      }

      screen.orientation = orientation

      /**
       * Set actual scrollbar width as a variable.
       * - This is used to apply as `padding-right` when body is scroll locked.
       * - This prevents the entire body shifting to the right when overlay appears.
       */
      let scrollbarWidth = window.innerWidth - document.body.clientWidth

      /**
       * - There is a bug (on Windows OS) when the scrollbar is calculated
       * incorrectly when you suddenly resize from Desktop -> to a Mobile device
       * - Typical sizes of scrollbars are in the range of 15-20 pixels on all
       * Desktop browsers / OS
       * - So we set the maximum limit value for it at 20px to handle worst-case
       * scenarios
       */
      if (scrollbarWidth > 20) {

        /**
         * - Mobile devices usually don't have an allocated space for a scrollbar
         * - So we reset to zero if an incorrect calculation occurred, since a
         * even few pixels value can still cause a CLS
         */
        const safeScrollbarWidth = screen.mobile ? 0 : 20
        scrollbarWidth = safeScrollbarWidth
      }

      /**
       * Update CSS properties.
       */
      document.documentElement.style.setProperty(
        '--viewport-height',
        `${screen.height}px`,
      )

      document.documentElement.style.setProperty(
        '--body-scroll-bar-width',
        `${scrollbarWidth}px`,
      )

      /**
       * Emit to EventBus.
       * - Used to sync Pinia state across all section instances.
       */
      mint.EventBus.dispatch('mint:resize', {
        screen: screen,
      })

      this.setScreen(screen)
    },
  },
})
